import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { signOut } from 'aws-auth-lib';

import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography
} from '@mui/material';

import { IconLogout, IconSettings, IconUser } from '@tabler/icons-react';
import { useUser } from '../../../../hooks/useUser';
import MainCard from '../../../../component/cards/MainCard';
import Transitions from '../../../../component/extended/Transitions';
import { resetUser } from '../../../../store/userReducer';
import { useRole } from '../../../../utils/RoleContext';
import { useClinic } from '../../../../utils/ClinicContext';

// assets

// ==============================|| PROFILE MENU ||============================== //

function ProfileSection() {
    const theme = useTheme();
    const { user } = useUser();
    const { setRole } = useRole();
    const { setClinic } = useClinic();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    // format strings
    //const greetingString = greetingsFormaters(user, true);

    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);
    const handleLogout = async () => {
        const userUuid = user.uuid;

        try {
            await signOut();
        } finally {
            dispatch(resetUser());
            setRole(null);
            setClinic(null);
            sessionStorage.removeItem('role');
            sessionStorage.removeItem('clinic');
            sessionStorage.removeItem('token');
            if (userUuid) {
                localStorage.removeItem(userUuid);
                localStorage.removeItem(`${userUuid}-clinic`);
            }
            queryClient.clear();
            navigate('/login', { replace: true });
        }
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '', prams = {}) => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route, prams);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    },
                    marginTop: 2
                }}
                icon={
                    <Avatar
                        src={user?.picture}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    />
                }
                label={
                    <IconSettings
                        stroke={1.5}
                        size="1.5rem"
                        color={theme.palette.primary.main}
                    />
                }
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                >
                                    <Box>
                                        <Typography variant="h4">{user.name}</Typography>
                                        <Box sx={{ height: 8 }} />
                                        <Divider />
                                    </Box>
                                    <Box>
                                        <List
                                            component="nav"
                                            sx={{
                                                width: '100%',
                                                maxWidth: 350,
                                                minWidth: 300,
                                                backgroundColor:
                                                    theme.palette.background.paper,
                                                borderRadius: '10px',
                                                [theme.breakpoints.down('md')]: {
                                                    minWidth: '100%'
                                                },
                                                '& .MuiListItemButton-root': {
                                                    mt: 0.5
                                                }
                                            }}
                                        >
                                            <ListItemButton
                                                sx={{ borderRadius: `5px` }}
                                                selected={selectedIndex === 0}
                                                onClick={(event) =>
                                                    handleListItemClick(
                                                        event,
                                                        0,
                                                        '/settings'
                                                    )
                                                }
                                                disabled={false}
                                            >
                                                <ListItemIcon>
                                                    <IconSettings
                                                        stroke={1.5}
                                                        size="1.3rem"
                                                        color={theme.palette.grey[700]}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2">
                                                            Definições
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            <ListItemButton
                                                sx={{ borderRadius: `5px` }}
                                                selected={selectedIndex === 1}
                                                onClick={(event) =>
                                                    handleListItemClick(
                                                        event,
                                                        1,
                                                        'profile',
                                                        { state: { user } }
                                                    )
                                                }
                                                disabled={false}
                                            >
                                                <ListItemIcon>
                                                    <IconUser
                                                        stroke={1.5}
                                                        size="1.3rem"
                                                        color={theme.palette.grey[700]}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2">
                                                            Perfil
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            <ListItemButton
                                                sx={{ borderRadius: `5px` }}
                                                selected={selectedIndex === 4}
                                                onClick={handleLogout}
                                            >
                                                <ListItemIcon>
                                                    <IconLogout
                                                        stroke={1.5}
                                                        size="1.3rem"
                                                        color={theme.palette.grey[700]}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2">
                                                            Terminar sessão
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                        </List>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
}

export default ProfileSection;
